import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const dealAnalysisApi = createApi({
  reducerPath: "dealAnalysisApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["DealAnalysis", "DealStats", "ComparisonGroups"],
  endpoints: (builder) => ({
    // Get all deal analyses
    getAllDealAnalyses: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.search) queryParams.append("search", params.search);
        if (params.favorites) queryParams.append("favorites", params.favorites);
        if (params.archived) queryParams.append("archived", params.archived);
        if (params.comparisonGroup) queryParams.append("comparisonGroup", params.comparisonGroup);
        if (params.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
        if (params.limit) queryParams.append("limit", params.limit);
        if (params.skip) queryParams.append("skip", params.skip);
        return `/deal-analysis?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "DealAnalysis", id: _id })),
              { type: "DealAnalysis", id: "LIST" },
            ]
          : [{ type: "DealAnalysis", id: "LIST" }],
    }),

    // Get single deal analysis
    getDealAnalysisById: builder.query({
      query: (id) => `/deal-analysis/${id}`,
      providesTags: (result, error, id) => [{ type: "DealAnalysis", id }],
    }),

    // Create deal analysis
    createDealAnalysis: builder.mutation({
      query: (dealData) => ({
        url: "/deal-analysis",
        method: "POST",
        body: dealData,
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Update deal analysis
    updateDealAnalysis: builder.mutation({
      query: ({ id, ...dealData }) => ({
        url: `/deal-analysis/${id}`,
        method: "PUT",
        body: dealData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "DealAnalysis", id },
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Delete deal analysis (hard delete)
    deleteDealAnalysis: builder.mutation({
      query: (id) => ({
        url: `/deal-analysis/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Archive deal analysis
    archiveDealAnalysis: builder.mutation({
      query: (id) => ({
        url: `/deal-analysis/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Restore archived deal
    restoreDealAnalysis: builder.mutation({
      query: (id) => ({
        url: `/deal-analysis/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Toggle favorite
    toggleFavorite: builder.mutation({
      query: (id) => ({
        url: `/deal-analysis/${id}/favorite`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "DealAnalysis", id },
        { type: "DealAnalysis", id: "LIST" },
      ],
    }),

    // Update status
    updateDealStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/deal-analysis/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "DealAnalysis", id },
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Quick calculate (no save)
    quickCalculate: builder.mutation({
      query: (dealData) => ({
        url: "/deal-analysis/calculate",
        method: "POST",
        body: dealData,
      }),
    }),

    // Get stats
    getDealAnalysisStats: builder.query({
      query: () => "/deal-analysis/stats",
      providesTags: [{ type: "DealStats" }],
    }),

    // Get comparison groups
    getComparisonGroups: builder.query({
      query: () => "/deal-analysis/comparison-groups",
      providesTags: [{ type: "ComparisonGroups" }],
    }),

    // Get deals in comparison group
    getComparisonDeals: builder.query({
      query: (groupName) => `/deal-analysis/comparison-groups/${encodeURIComponent(groupName)}`,
      providesTags: (result, error, groupName) => [
        { type: "ComparisonGroups", id: groupName },
      ],
    }),

    // Add to comparison group
    addToComparisonGroup: builder.mutation({
      query: ({ id, groupName }) => ({
        url: `/deal-analysis/${id}/comparison-group`,
        method: "POST",
        body: { groupName },
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "ComparisonGroups" },
      ],
    }),

    // Remove from comparison group
    removeFromComparisonGroup: builder.mutation({
      query: (id) => ({
        url: `/deal-analysis/${id}/comparison-group`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "ComparisonGroups" },
      ],
    }),

    // Duplicate deal
    duplicateDeal: builder.mutation({
      query: ({ id, name }) => ({
        url: `/deal-analysis/${id}/duplicate`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),

    // Bulk archive
    bulkArchive: builder.mutation({
      query: (dealIds) => ({
        url: "/deal-analysis/bulk-archive",
        method: "POST",
        body: { dealIds },
      }),
      invalidatesTags: [
        { type: "DealAnalysis", id: "LIST" },
        { type: "DealStats" },
      ],
    }),
  }),
});

export const {
  useGetAllDealAnalysesQuery,
  useGetDealAnalysisByIdQuery,
  useCreateDealAnalysisMutation,
  useUpdateDealAnalysisMutation,
  useDeleteDealAnalysisMutation,
  useArchiveDealAnalysisMutation,
  useRestoreDealAnalysisMutation,
  useToggleFavoriteMutation,
  useUpdateDealStatusMutation,
  useQuickCalculateMutation,
  useGetDealAnalysisStatsQuery,
  useGetComparisonGroupsQuery,
  useGetComparisonDealsQuery,
  useAddToComparisonGroupMutation,
  useRemoveFromComparisonGroupMutation,
  useDuplicateDealMutation,
  useBulkArchiveMutation,
} = dealAnalysisApi;
